// SignalingClient — WebSocket 信令客户端
// 镜像 LocalSend signaling.rs:SignalingConnection 的客户端逻辑
//
// 流程：
// 1. connect(roomId, info) 建立 WS
// 2. 收到 HELLO → 拿到自己的 client.id 和 peers 列表
// 3. 后续收 JOIN/LEFT/UPDATE/OFFER/ANSWER/ERROR
// 4. sendOffer/sendAnswer/sendUpdate 通过 WS 发送
// 5. 自带重连和心跳

import {
  type ClientInfo,
  type ClientInfoWithoutId,
  type WsClientMessage,
  type WsServerMessage,
  buildWsUrl,
} from '@kuaichuan/shared';
import { workerWsUrl } from '../config';

export interface SignalingClientHandlers {
  onHello?: (client: ClientInfo, peers: ClientInfo[]) => void;
  onJoin?: (peer: ClientInfo) => void;
  onLeft?: (peerId: string) => void;
  onUpdate?: (peer: ClientInfo) => void;
  onOffer?: (peer: ClientInfo, sessionId: string, sdp: string) => void;
  onAnswer?: (peer: ClientInfo, sessionId: string, sdp: string) => void;
  onError?: (code: number) => void;
  onClose?: (event: CloseEvent) => void;
  onStateChange?: (state: 'connecting' | 'open' | 'closed') => void;
}

export class SignalingClient {
  private ws: WebSocket | null = null;
  private handlers: SignalingClientHandlers;
  private selfClient: ClientInfo | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectAttempts = 0;
  private shouldReconnect = true;
  private connectParams: { roomId: string; info: ClientInfoWithoutId } | null = null;

  constructor(handlers: SignalingClientHandlers) {
    this.handlers = handlers;
  }

  get self(): ClientInfo | null {
    return this.selfClient;
  }

  async connect(roomId: string, info: ClientInfoWithoutId): Promise<ClientInfo> {
    this.connectParams = { roomId, info };
    this.shouldReconnect = true;
    return this.doConnect(roomId, info);
  }

  private doConnect(roomId: string, info: ClientInfoWithoutId): Promise<ClientInfo> {
    return new Promise((resolve, reject) => {
      const url = buildWsUrl(workerWsUrl('/api/signaling/ws'), roomId, info);
      this.handlers.onStateChange?.('connecting');
      const ws = new WebSocket(url);
      this.ws = ws;

      ws.addEventListener('open', () => {
        this.handlers.onStateChange?.('open');
        this.reconnectAttempts = 0;
        this.startPing();
      });

      ws.addEventListener('message', (event) => {
        try {
          const msg = JSON.parse(event.data) as WsServerMessage;
          this.handleMessage(msg, resolve);
        } catch (err) {
          console.error('[signaling] parse error', err);
        }
      });

      ws.addEventListener('close', (event) => {
        this.handlers.onStateChange?.('closed');
        this.stopPing();
        this.handlers.onClose?.(event);
        if (this.shouldReconnect && this.reconnectAttempts < 3) {
          this.reconnectAttempts++;
          const delay = Math.min(1000 * 2 ** this.reconnectAttempts, 10000);
          setTimeout(() => {
            if (this.connectParams && this.shouldReconnect) {
              this.doConnect(this.connectParams.roomId, this.connectParams.info).catch(() => {
                /* 重连失败保持静默，等待下一次 */
              });
            }
          }, delay);
        }
      });

      ws.addEventListener('error', (err) => {
        console.error('[signaling] ws error', err);
        if (this.reconnectAttempts === 0) {
          reject(new Error('WebSocket 连接失败'));
        }
      });
    });
  }

  private handleMessage(msg: WsServerMessage, resolveHello?: (c: ClientInfo) => void) {
    switch (msg.type) {
      case 'HELLO':
        this.selfClient = msg.client;
        this.handlers.onHello?.(msg.client, msg.peers);
        resolveHello?.(msg.client);
        break;
      case 'JOIN':
        this.handlers.onJoin?.(msg.peer);
        break;
      case 'LEFT':
        this.handlers.onLeft?.(msg.peerId);
        break;
      case 'UPDATE':
        this.handlers.onUpdate?.(msg.peer);
        break;
      case 'OFFER':
        this.handlers.onOffer?.(msg.peer, msg.sessionId, msg.sdp);
        break;
      case 'ANSWER':
        this.handlers.onAnswer?.(msg.peer, msg.sessionId, msg.sdp);
        break;
      case 'ERROR':
        this.handlers.onError?.(msg.code);
        break;
    }
  }

  private startPing() {
    this.stopPing();
    this.pingTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        // 协议层无 ping 消息，靠 WS 自带 ping/pong；这里仅检测状态
      }
    }, 30_000);
  }

  private stopPing() {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  send(msg: WsClientMessage) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  sendUpdate(info: ClientInfoWithoutId) {
    this.send({ type: 'UPDATE', info });
  }

  sendOffer(sessionId: string, target: string, sdp: string) {
    this.send({ type: 'OFFER', sessionId, target, sdp });
  }

  sendAnswer(sessionId: string, target: string, sdp: string) {
    this.send({ type: 'ANSWER', sessionId, target, sdp });
  }

  disconnect() {
    this.shouldReconnect = false;
    this.stopPing();
    if (this.ws) {
      this.ws.close(1000, 'client disconnect');
      this.ws = null;
    }
    this.selfClient = null;
  }
}
